import { useEffect, useRef, useState, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import { classNames } from 'primereact/utils';
import { Link, ScrollRestoration, useLocation } from 'react-router-dom';

import { useAppVersion } from '@/core/version/AppVersionContext';
import { usePermissionsForUser } from '@/modules/access/hooks/permissions';
import { useCanUseV2 } from '@/modules/access/hooks/useCanUseV2';
import TopBar from '@/shared/layout/TopBar';
import { useMedicalCenters } from '@modules/hooks/medicalCenterHooks';

import { dashboardItems, UserRole } from '../constants';
import { useClientList, useVersion } from '../hooks/queries';
import SideBar from './components/sidebar/SideBar';
import SideBarItem from './components/sidebar/SideBarItem';
import SideBarLabelGroup from './components/sidebar/SideBarLabelGroup';
import './Dashboard.css';

/**
 * Main dashboard box
 * @param {object} props
 * @param {string} props.headerTitle
 * @param {string | undefined} props.backButtonUrl
 * @param {string | undefined} props.fixedWidth
 * @param {boolean?} props.dynamicGoBack
 * @param {boolean?} props.withPannel
 * @param {React.ReactNode} props.children
 */
export default function Dashboard({
  headerTitle,
  backButtonUrl,
  fixedWidth,
  children,
  panelClassName,
  dynamicGoBack = false,
  withPannel = true,
}) {
  const location = useLocation();
  const userColectivo = localStorage.getItem('fund_type') || 'prepay';
  const isSuperUser = localStorage.getItem('is_superuser') === 'true';

  const permissions = usePermissionsForUser();
  const { isV2, setVersion } = useAppVersion();
  const { canUseV2, isLoading: canUseV2Loading } = useCanUseV2();

  useEffect(() => {
    if (!canUseV2Loading && !canUseV2 && isV2) {
      setVersion('v1');
    }
  }, [canUseV2, canUseV2Loading, isV2, setVersion]);

  const authQuery = useQuery({
    queryKey: ['auth'],
    queryFn: () => ({
      isAdmin: localStorage.getItem('isAdmin') === 'true',
      role: localStorage.getItem('role'),
      token: localStorage.getItem('token'),
      username: localStorage.getItem('username'),
    }),
    // treat this cache as source of truth until updated via setQueryData in Login
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isExpandedSideBar, setIsExpandedSideBar] = useState(() => {
    if (window.innerWidth <= 768) {
      return false;
    }

    const stored = localStorage.getItem('isExpandedSideBar');
    return stored !== null ? stored === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('isExpandedSideBar', isExpandedSideBar);
  }, [isExpandedSideBar]);

  const role = localStorage.getItem('role');

  const {
    data: medicalCenters,
    isLoading: isMedicalCentersLoading,
    isFetching: isMedicalCentersFetching,
  } = useMedicalCenters({
    clientID: null,
    enabled: role === UserRole.MEDICAL_CENTER,
  });
  const {
    data: clients,
    isLoading: isClientsLoading,
    isFetching: isClientsFetching,
  } = useClientList();

  const userName = localStorage.getItem('username');

  const useVersionQuery = useVersion();
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [updatePayload, setUpdatePayload] = useState(null);
  const [pendingServerVersion, setUpdateServerVersion] = useState(null);
  const lastHandledVersionRef = useRef(null);

  useEffect(() => {
    if (role === UserRole.MEDICAL_CENTER && medicalCenters && medicalCenters.length > 0) {
      // Asumiendo que solo hay un centro médico por usuario
      localStorage.setItem(
        'can_create_case',
        medicalCenters.some(mc => mc.can_create_case) ? 'true' : 'false'
      );
    }
  }, [medicalCenters, role]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    console.debug('[ModalB] useVersionQuery status', {
      status: useVersionQuery.status,
      isSuccess: useVersionQuery.isSuccess,
      isError: useVersionQuery.isError,
      data: useVersionQuery.data,
    });
  }, [useVersionQuery.status, useVersionQuery.data]);

  useEffect(() => {
    if (!useVersionQuery.isSuccess) return;

    const raw = useVersionQuery.data;
    const serverVersion =
      typeof raw === 'string'
        ? raw
        : (raw?.version ?? raw?.dashboard_version ?? raw?.version_name ?? '');
    if (!serverVersion) return;

    if (lastHandledVersionRef.current === serverVersion) return;

    const storedVersion = localStorage.getItem('dashboard_version') || '';

    console.debug('[ModalB] version compare', {
      serverVersion,
      storedVersion,
      same: storedVersion === serverVersion,
      hasPayload: !!raw?.payload,
    });

    if (storedVersion !== serverVersion) {
      setUpdatePayload(raw?.payload ?? null);
      setUpdateServerVersion(serverVersion);
      setShowPayloadModal(true);
    }

    lastHandledVersionRef.current = serverVersion;
  }, [useVersionQuery.isSuccess, useVersionQuery.data?.version]);

  const handleUpdatePayloadModal = () => {
    if (pendingServerVersion) {
      localStorage.setItem('dashboard_version', String(pendingServerVersion));
    }
    window.location.reload();
  };

  const handleClosePayloadModal = () => {
    handleUpdatePayloadModal();
  };
  // Compute filtered dashboard items immutably so React re-renders predictably
  const dashboardItemsFiltered = useMemo(() => {
    const roleVal = authQuery.data?.role || localStorage.getItem('role');
    const isAdmin_ = Boolean(authQuery.data?.isAdmin);

    // start from base items, apply colectivo filter when not superuser
    let base = dashboardItems;
    if (!isSuperUser) {
      base = dashboardItems.filter(g => {
        return userColectivo === 'prepay' ? g.label !== 'Abonos' : g.label !== 'Anticipos';
      });
    }

    const hasPerms = permissions && !permissions.isLoading && Array.isArray(permissions.data);

    const evaluateRoute = routeItem => {
      if (!routeItem) return false;
      if (routeItem.url === '/logout') return true;
      if (!roleVal) return false;
      if (isAdmin_) return true;

      if (hasPerms) {
        if (routeItem.permission && permissions.data.includes(routeItem.permission)) return true;
        const routePerm = `dashboard:view:${routeItem.url}`;
        if (permissions.data.includes(routePerm)) return true;
      } else {
        if (permissions && permissions.isLoading) return true;
      }

      if (clients && clients.length > 0) {
        if (
          medicalCenters &&
          roleVal === UserRole.MEDICAL_CENTER &&
          routeItem.url === '/casos/nuevo'
        ) {
          if (medicalCenters.some(mc => mc.can_create_case)) return true;
        }

        if (routeItem.user && routeItem.clients) {
          if (!routeItem.user.includes(roleVal)) return false;
          return routeItem.clients.some(client =>
            clients.some(userClient => userClient.name === client)
          );
        }
      }

      return false;
    };

    // build new array with filtered items (do not mutate original)
    return base
      .map(group => {
        const items = (group.items || []).filter(ri => evaluateRoute(ri));
        return { ...group, items };
      })
      .filter(g => g.items && g.items.length > 0);
    // dependencies: anything that affects permission evaluation
  }, [
    authQuery.data?.isAdmin,
    authQuery.data?.role,
    permissions?.data,
    permissions?.isLoading,
    clients,
    medicalCenters,
    isSuperUser,
    userColectivo,
  ]);

  return (
    <div className="layout-dashboard">
      <SideBar
        isExpandedSideBar={isExpandedSideBar}
        setIsExpandedSideBar={setIsExpandedSideBar}
        userName={userName}
        // show loader only when data is not available at all (avoid flicker on navigation)
        isLoading={
          (isClientsFetching && !clients) ||
          (permissions.isFetching && !permissions.data) ||
          (isMedicalCentersFetching && !medicalCenters)
        }
        isMobile={isMobile}
      >
        {dashboardItemsFiltered
          .filter(routeGroup => routeGroup.items.length > 0)
          .map(routeGroup => (
            <SideBarLabelGroup label={routeGroup.label} key={routeGroup.label}>
              {routeGroup.items.map(item => (
                <SideBarItem key={item.url} item={item} />
              ))}
            </SideBarLabelGroup>
          ))}
      </SideBar>

      <div className={isExpandedSideBar ? 'layout-content' : 'layout-content layout-inactive'}>
        {/* Barra superior */}
        <TopBar
          isMobile={isMobile}
          onClickMobileMenu={() => setIsExpandedSideBar(!isExpandedSideBar)}
          headerTitle={headerTitle}
        ></TopBar>

        <div
          className={classNames('layout-main-container', {
            'p-0': location.pathname === '/chat',
          })}
        >
          <div className="layout-main">
            {backButtonUrl && (
              <Link to={backButtonUrl} className="back-link">
                <i className="pi pi-chevron-left" aria-hidden="true"></i>
                <span>Volver</span>
              </Link>
            )}
            {dynamicGoBack && (
              <span
                onClick={() => window.history.back()}
                id="backg"
                className="back-link cursor-pointer"
              >
                <i className="pi pi-chevron-left" aria-hidden="true"></i>
                <span>Volver</span>
              </span>
            )}
            {withPannel ? (
              <Panel
                className={panelClassName ? panelClassName : 'w-min md:w-12'}
                header={''}
                style={fixedWidth ? { width: fixedWidth } : {}}
              >
                {children}
              </Panel>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
      <ScrollRestoration />

      <Dialog
        header="Actualización"
        visible={showPayloadModal}
        onHide={e => {
          if (e?.method === 'close-icon' || e?.method === 'mask') return;
          handleClosePayloadModal();
        }}
        closable={false}
        dismissableMask={false}
        style={{ width: '60vw' }}
      >
        {updatePayload ? (
          <div>
            <div className="flex flex-column align-items-center justify-content-center">
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                {updatePayload.title || 'Novedades'}
              </div>
              {updatePayload.subtitle && (
                <div style={{ color: '#6b7280', marginTop: 6 }}>{updatePayload.subtitle}</div>
              )}
            </div>
            <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
              {(updatePayload.items || []).map((card, idx) => (
                <div
                  key={`version-card-${idx}`}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{card.header}</div>
                  <div style={{ color: '#374151', marginBottom: 8 }}>
                    {(card.items && card.items[0]) || ''}
                  </div>
                  {card.images && card.images.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {card.images.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt={`v-img-${i}`}
                          style={{
                            width: 140,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 6,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-column align-items-center justify-content-center">
            <p>No hay información disponible.</p>
          </div>
        )}
        <div className="flex justify-content-end" style={{ marginTop: 12 }}>
          <Button label="Actualizar" icon="pi pi-refresh" onClick={handleUpdatePayloadModal} />
        </div>
      </Dialog>
    </div>
  );
}
